import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface SourceState {
  currentPage: number;
  totalPages: number;
  zoom: number;
  rotation: number;
  isFullscreen: boolean;
  showThumbnails: boolean;
  showSearch: boolean;
  pdfTheme: 'light' | 'dark';
  fileUrl: string | null;
  documentId: string | null;
}

interface SourceActions {
  setCurrentPage: (page: number) => void;
  setTotalPages: (pages: number) => void;
  setZoom: (zoom: number) => void;
  setRotation: (rotation: number) => void;
  toggleFullscreen: () => void;
  toggleThumbnails: () => void;
  toggleSearch: () => void;
  togglePdfTheme: () => void;
  setFileUrl: (url: string | null) => void;
  setDocumentId: (id: string | null) => void;
  reset: () => void;
}

const initialState: SourceState = {
  currentPage: 1,
  totalPages: 0,
  zoom: 1,
  rotation: 0,
  isFullscreen: false,
  showThumbnails: false,
  showSearch: false,
  pdfTheme: 'light',
  fileUrl: null,
  documentId: null,
};

export const useSourceStore = create<SourceState & SourceActions>()(
  persist(
    (set) => ({
      ...initialState,

      setCurrentPage: (page) => set({ currentPage: page }),
      setTotalPages: (pages) => set({ totalPages: pages }),
      setZoom: (zoom) => set({ zoom }),
      setRotation: (rotation) => set({ rotation: rotation % 360 }),
      toggleFullscreen: () => set((state) => ({ isFullscreen: !state.isFullscreen })),
      toggleThumbnails: () => set((state) => ({ showThumbnails: !state.showThumbnails })),
      toggleSearch: () => set((state) => ({ showSearch: !state.showSearch })),
      togglePdfTheme: () =>
        set((state) => ({ pdfTheme: state.pdfTheme === 'light' ? 'dark' : 'light' })),
      setFileUrl: (url) => set({ fileUrl: url }),
      setDocumentId: (id) => set({ documentId: id }),
      reset: () => set(initialState),
    }),
    {
      name: 'examing-source',
      storage: createJSONStorage(() => localStorage),
      // Only persist the theme preference; everything else is session-specific
      partialize: (state) => ({ pdfTheme: state.pdfTheme }),
    }
  )
);
